import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { PhoneNumberCard } from "@/components/admin/PhoneNumberCard";
import { Loader2 } from "lucide-react";

interface PhoneNumberWithStats {
  id: string;
  twilio_phone_number: string;
  display_name: string;
  is_active: boolean;
  county: string;
  unread_count: number;
  last_message_at: string | null;
  sms_settings: {
    sms_enabled: boolean;
  } | null;
}

const SMSPhoneNumbers = () => {
  const { user, loading: authLoading } = useAuth();
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumberWithStats[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadPhoneNumbers();
      
      // Subscribe to real-time updates
      const channel = supabase
        .channel('phone-numbers-updates')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'sms_conversations' }, () => {
          loadPhoneNumbers();
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'phone_numbers' }, () => {
          loadPhoneNumbers();
        })
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'sms_messages' }, () => {
          loadPhoneNumbers();
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'sms_settings' }, () => {
          loadPhoneNumbers();
        })
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user]);

  const loadPhoneNumbers = async () => {
    try {
      // Get all phone numbers with their SMS settings
      const { data: phoneData, error: phoneError } = await supabase
        .from('phone_numbers')
        .select(`
          id,
          twilio_phone_number,
          display_name,
          is_active,
          county,
          sms_settings!phone_number_id (
            sms_enabled
          )
        `)
        .order('display_name');

      if (phoneError) throw phoneError;

      // Get unread counts and last message times for each phone number
      const phoneNumbersWithStats = await Promise.all(
        (phoneData || []).map(async (phone: any) => {
          const { data: conversations, error: convError } = await supabase
            .from('sms_conversations')
            .select('unread_count, last_message_at')
            .eq('phone_number_id', phone.id)
            .eq('status', 'active');

          if (convError) {
            console.error('Error loading conversations:', convError);
          return {
              ...phone,
              unread_count: 0,
              last_message_at: null
            };
          }

          const totalUnread = conversations?.reduce((sum, conv) => sum + (conv.unread_count || 0), 0) || 0;
          const lastMessage = conversations?.reduce((latest, conv) => {
            if (!latest) return conv.last_message_at;
            if (!conv.last_message_at) return latest;
            return new Date(conv.last_message_at) > new Date(latest) ? conv.last_message_at : latest;
          }, null as string | null);

          return {
            ...phone,
            unread_count: totalUnread,
            last_message_at: lastMessage
          };
        })
      );

      setPhoneNumbers(phoneNumbersWithStats);
    } catch (error) {
      console.error('Error loading phone numbers:', error);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <>
      <SEOHead 
        title="SMS Phone Numbers"
        description="Manage SMS-enabled phone numbers and conversations"
      />
      
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">SMS Phone Numbers</h1>
          <p className="text-muted-foreground mt-2">
            Select a phone number to view and manage conversations
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : phoneNumbers.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            No phone numbers found
          </div>
        ) : (
          <div className="grid gap-4">
            {phoneNumbers.map((phoneNumber) => (
              <PhoneNumberCard 
                key={phoneNumber.id} 
                phoneNumber={phoneNumber}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default SMSPhoneNumbers;
